/**
 * E.164 phone helpers — the single source of truth for every contact-capture
 * surface (`phone-india.ts` is a thin India-only wrapper over this module).
 *
 * WHY a hand-rolled registry instead of libphonenumber: libphonenumber-js ships
 * ~145KB of metadata for full national-format parsing we do not need. Our forms
 * only ever ask "is this a plausible mobile for the country the seeker picked",
 * so a ~20-country digit-bounds registry answers it at a fraction of the bundle.
 * The set covers India plus the NRI diaspora markets that actually pay (real
 * captured +971/+1/+60 buyers exist in vedic_report_purchases).
 *
 * INDIA IS BYTE-FOR-BYTE UNCHANGED. The IN entry carries the exact historical
 * rule (10 digits starting 6-9) as an explicit `pattern`, and every India path
 * through this module returns what `normalizeIndianPhone` always returned — the
 * canonical `+91XXXXXXXXXX` — for the same lenient real-world inputs (a pasted
 * `+91 98765 43210`, `0091…`, a trunk `0`, spaces/dashes).
 *
 * Pure and dependency-free so the web coverage ratchet (~93% funcs / 81%
 * branches over src/lib/**) stays green — an untested lib file tips the GLOBAL
 * gate red and blocks all deploys.
 */

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 code. */
  iso2: string;
  /** E.164 calling code, with the leading "+". */
  dialCode: string;
  /** Display name for the picker. */
  label: string;
  /** Flag emoji for the picker. */
  flag: string;
  /** Inclusive national-significant-number digit bounds (mobile). */
  nationalDigits: { min: number; max: number };
  /**
   * Optional exact national-number rule, used when digit bounds are too loose.
   * India keeps its historical `[6-9]\d{9}` mobile-series rule here so the
   * widening to E.164 cannot silently start accepting Indian landlines.
   */
  pattern?: RegExp;
}

/**
 * IN first (the default and ~93% of traffic), then the diaspora markets ordered
 * by NRI population. Digit bounds are mobile-number lengths; they intentionally
 * do not encode area-code rules — the goal is rejecting typos, not validating
 * carrier assignment.
 */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = [
  {
    iso2: "IN",
    dialCode: "+91",
    label: "India",
    flag: "🇮🇳",
    nationalDigits: { min: 10, max: 10 },
    pattern: /^[6-9]\d{9}$/,
  },
  {
    iso2: "US",
    dialCode: "+1",
    label: "United States",
    flag: "🇺🇸",
    nationalDigits: { min: 10, max: 10 },
  },
  {
    iso2: "AE",
    dialCode: "+971",
    label: "United Arab Emirates",
    flag: "🇦🇪",
    nationalDigits: { min: 8, max: 9 },
  },
  {
    iso2: "GB",
    dialCode: "+44",
    label: "United Kingdom",
    flag: "🇬🇧",
    nationalDigits: { min: 9, max: 10 },
  },
  { iso2: "CA", dialCode: "+1", label: "Canada", flag: "🇨🇦", nationalDigits: { min: 10, max: 10 } },
  {
    iso2: "AU",
    dialCode: "+61",
    label: "Australia",
    flag: "🇦🇺",
    nationalDigits: { min: 9, max: 9 },
  },
  {
    iso2: "SA",
    dialCode: "+966",
    label: "Saudi Arabia",
    flag: "🇸🇦",
    nationalDigits: { min: 9, max: 9 },
  },
  {
    iso2: "MY",
    dialCode: "+60",
    label: "Malaysia",
    flag: "🇲🇾",
    nationalDigits: { min: 9, max: 10 },
  },
  {
    iso2: "SG",
    dialCode: "+65",
    label: "Singapore",
    flag: "🇸🇬",
    nationalDigits: { min: 8, max: 8 },
  },
  { iso2: "QA", dialCode: "+974", label: "Qatar", flag: "🇶🇦", nationalDigits: { min: 8, max: 8 } },
  { iso2: "KW", dialCode: "+965", label: "Kuwait", flag: "🇰🇼", nationalDigits: { min: 8, max: 8 } },
  { iso2: "OM", dialCode: "+968", label: "Oman", flag: "🇴🇲", nationalDigits: { min: 8, max: 8 } },
  {
    iso2: "BH",
    dialCode: "+973",
    label: "Bahrain",
    flag: "🇧🇭",
    nationalDigits: { min: 8, max: 8 },
  },
  {
    iso2: "NZ",
    dialCode: "+64",
    label: "New Zealand",
    flag: "🇳🇿",
    nationalDigits: { min: 8, max: 10 },
  },
  {
    iso2: "NP",
    dialCode: "+977",
    label: "Nepal",
    flag: "🇳🇵",
    nationalDigits: { min: 10, max: 10 },
  },
  {
    iso2: "LK",
    dialCode: "+94",
    label: "Sri Lanka",
    flag: "🇱🇰",
    nationalDigits: { min: 9, max: 9 },
  },
  {
    iso2: "IE",
    dialCode: "+353",
    label: "Ireland",
    flag: "🇮🇪",
    nationalDigits: { min: 9, max: 9 },
  },
  { iso2: "IT", dialCode: "+39", label: "Italy", flag: "🇮🇹", nationalDigits: { min: 9, max: 10 } },
  {
    iso2: "DE",
    dialCode: "+49",
    label: "Germany",
    flag: "🇩🇪",
    nationalDigits: { min: 10, max: 11 },
  },
  { iso2: "FR", dialCode: "+33", label: "France", flag: "🇫🇷", nationalDigits: { min: 9, max: 9 } },
  {
    iso2: "NL",
    dialCode: "+31",
    label: "Netherlands",
    flag: "🇳🇱",
    nationalDigits: { min: 9, max: 9 },
  },
];

export const DEFAULT_COUNTRY_ISO2 = "IN";

/** Dial codes longest-first so `+971…` can never be mis-read as `+9` / `+1`. */
const BY_DIAL_LENGTH: readonly PhoneCountry[] = [...PHONE_COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

/** The IN entry — the guaranteed fallback for every "unknown country" path. */
const INDIA = PHONE_COUNTRIES[0];

function onlyDigits(input: string): string {
  return input.replace(/\D/g, "");
}

/** Look up a country by ISO2 code (case-insensitive). Returns null if absent. */
export function findCountry(iso2: string | null | undefined): PhoneCountry | null {
  if (!iso2) return null;
  const wanted = iso2.trim().toUpperCase();
  return PHONE_COUNTRIES.find((c) => c.iso2 === wanted) ?? null;
}

/** Look up a country by ISO2, falling back to India. Never returns null. */
export function countryOrDefault(iso2: string | null | undefined): PhoneCountry {
  return findCountry(iso2) ?? INDIA;
}

/** `composeE164("+91", "98765 43210")` → `"+919876543210"`. */
export function composeE164(dialCode: string, national: string): string {
  return `${dialCode}${onlyDigits(national)}`;
}

/**
 * Split an international-shaped input (`+…` or `00…`) into its country and
 * national part. Matches the LONGEST dial code, so `+971…` resolves to AE.
 *
 * `+1` is shared by US and CA; the first registry entry (US) wins. Only the
 * picker's displayed flag is affected — the resulting E.164 string is identical
 * either way, which is all the wire and WhatsApp care about.
 */
export function parseE164(input: string): { country: PhoneCountry | null; national: string } {
  let digits = onlyDigits(input);
  if (digits.startsWith("00")) digits = digits.slice(2);
  const country = BY_DIAL_LENGTH.find((c) => digits.startsWith(c.dialCode.slice(1))) ?? null;
  if (!country) return { country: null, national: digits };
  return { country, national: digits.slice(country.dialCode.length - 1) };
}

/**
 * True when `national` is a plausible mobile for `country`. Uses the country's
 * exact `pattern` when it has one (India), else its digit bounds.
 */
export function isValidE164ForCountry(country: PhoneCountry, national: string): boolean {
  const digits = onlyDigits(national);
  if (country.pattern) return country.pattern.test(digits);
  return digits.length >= country.nationalDigits.min && digits.length <= country.nationalDigits.max;
}

/**
 * Strip a raw input down to `country`'s national-significant number, dropping a
 * leading `00` international prefix, then the country's dial code, then a trunk
 * `0` — in that order — whenever doing so leaves more than the national maximum.
 *
 * This is the historical India `nationalDigits` algorithm with the hardcoded 10
 * replaced by `country.nationalDigits.max`; for IN (max 10) it is identical.
 */
export function toNationalDigits(country: PhoneCountry, input: string): string {
  let digits = onlyDigits(input);
  const max = country.nationalDigits.max;
  const dial = country.dialCode.slice(1);
  if (digits.length > max && digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length > max && digits.startsWith(dial)) digits = digits.slice(dial.length);
  if (digits.length > max && digits.startsWith("0")) digits = digits.slice(1);
  return digits;
}

/** Keep only the typed national digits (capped at the country max) for a controlled input. */
export function formatNationalInput(country: PhoneCountry, input: string): string {
  return toNationalDigits(country, input).slice(0, country.nationalDigits.max);
}

/**
 * Normalize any real-world input to canonical E.164, or null when it is not a
 * plausible mobile.
 *
 * An input carrying an explicit international prefix (`+…` / `00…`) is resolved
 * against the registry by dial code — so a seeker who pastes `+14155552671`
 * into an India-defaulted field still gets the right answer. A bare national
 * number is interpreted as `defaultCountry`, which keeps the historical India
 * behaviour intact: `9876543210` → `+919876543210`.
 */
export function normalizePhoneE164(
  input: string,
  defaultCountry: string = DEFAULT_COUNTRY_ISO2
): string | null {
  const raw = String(input ?? "").trim();
  if (!raw) return null;
  const country = countryOrDefault(defaultCountry);

  if (/^\+/.test(raw) || onlyDigits(raw).startsWith("00")) {
    const parsed = parseE164(raw);
    if (!parsed.country || !isValidE164ForCountry(parsed.country, parsed.national)) return null;
    return composeE164(parsed.country.dialCode, parsed.national);
  }

  const national = toNationalDigits(country, raw);
  if (!isValidE164ForCountry(country, national)) return null;
  return composeE164(country.dialCode, national);
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Optional email: empty is allowed (returns true); a non-empty value must be a
 * basic `local@domain.tld` shape.
 */
export function isValidOptionalEmail(input: string): boolean {
  const v = input.trim();
  if (!v) return true;
  return EMAIL_RE.test(v);
}
